// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { ReactNode, useEffect } from 'react'
import { Link } from 'react-router-dom'

// ============================================================================
// OwlRx Webhook API — receiver specification. PUBLIC (no auth, no shell), same as /legal/*, so a
// partner can be sent one link. It is the contract a receiving endpoint is built against: every
// header name, status rule and hash rule here must match functions/DataApi/WebhookSender.cs and
// WebhookDeliveryWorker.cs. Change the sender and this page together, never one alone —
// PR #2651 renamed the hash header in code only and every receiver built to spec broke.
// ============================================================================

// Portal styling: the same theme tokens as the app's screens (Inter, stone surfaces, teal primary,
// --radius-*), so a partner lands on something that looks like OwlRx. The one PDF element kept is
// the official Owl Consultations logo, centred above the title the way the branded pharmacy login
// shows a pharmacy's logo.
const pageWrap: React.CSSProperties = { maxWidth: 880, margin: '0 auto', padding: '40px 24px 64px', color: 'var(--color-text)' }
const h1: React.CSSProperties = { fontSize: 28, fontWeight: 700, lineHeight: 1.25, color: 'var(--color-text)', margin: '0 0 8px', textAlign: 'center' }
const h2: React.CSSProperties = { fontSize: 19, fontWeight: 650, color: 'var(--color-text)', marginTop: 36, marginBottom: 10, scrollMarginTop: 16 }
const h3: React.CSSProperties = { fontSize: 15.5, fontWeight: 600, color: 'var(--color-text)', marginTop: 22, marginBottom: 8 }
const p: React.CSSProperties = { fontSize: 14, lineHeight: 1.7, color: 'var(--color-text)', marginBottom: 12 }
const li: React.CSSProperties = { fontSize: 14, lineHeight: 1.7, color: 'var(--color-text)', marginBottom: 6 }
const codeStyle: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12.5, background: 'var(--color-surface-alt)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0 5px' }
const pre: React.CSSProperties = { ...codeStyle, display: 'block', padding: '12px 14px', borderRadius: 'var(--radius-md, 8px)', overflowX: 'auto', lineHeight: 1.6, margin: '0 0 14px' }
const card: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg, 12px)' }
const th: React.CSSProperties = { textAlign: 'left', fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--color-text-muted)', padding: '9px 12px', background: 'var(--color-surface-alt)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' }
const td: React.CSSProperties = { fontSize: 13.5, lineHeight: 1.6, padding: '9px 12px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top', color: 'var(--color-text)' }
// Links inside the document only — the portal link colour, scoped so the rest of the app is untouched.
const DOC_CSS = `.owl-doc a { color: var(--color-primary); text-decoration: none; font-weight: 500 } .owl-doc a:hover { text-decoration: underline }`

const C = ({ children }: { children: ReactNode }) => <code style={codeStyle}>{children}</code>

/**
 * A static reference table in a spec — a handful of fixed rows, not records, so it gets no column
 * funnels (D-UI-2). The header row is <th scope="col"> cells inside <tbody> rather than a <thead>:
 * screen readers still announce them as column headers, and AutoTableFunnels only enhances tables
 * with a <thead>. Do not reuse this for data lists — those must be funnelled.
 */
function RefTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div style={{ ...card, overflowX: 'auto', marginBottom: 16 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <tbody>
          <tr>{head.map(h => <th key={h} scope="col" style={th}>{h}</th>)}</tr>
          {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} style={td}>{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  )
}

const SECTIONS = [
  ['endpoint', 'Endpoint requirements'],
  ['what-we-send', 'What we send'],
  ['integrity', 'Integrity check'],
  ['response', 'Your response'],
  ['delivery', 'Delivery behaviour'],
  ['security', 'Security and data handling'],
] as const

const ACK_EXAMPLE = `{
  "received": true,
  "message_id": "<MessageHeader.MessageID>",
  "checksum": "<X-OwlRx-Envelope value>",
  "received_at": "<ISO-8601 UTC timestamp>"
}`

export default function WebhookApiDocsPage() {
  useEffect(() => { document.title = 'Webhook API — OwlRx' }, [])
  return (
    <div className="owl-doc" style={{ background: 'var(--color-bg)', minHeight: '100vh' }}>
    <style>{DOC_CSS}</style>
    <main style={pageWrap}>
      <header style={{ textAlign: 'center', padding: '0 0 28px' }}>
        {/* The official Owl Consultations mark (shield, wordmark, tagline, service line) — not the
            small owl.svg app icon. */}
        <img src="/owl-consultations-logo.png" width={260} height={172}
          alt="Owl Consultations — we see what others can’t. Managed Services, Cyber Security, Compliance, Software Development"
          style={{ maxWidth: '100%', height: 'auto', marginBottom: 20 }} />
        <h1 style={h1}>OwlRx Webhook API</h1>
        <p style={{ ...p, color: 'var(--color-text-muted)', maxWidth: 620, margin: '0 auto' }}>
          Technical documentation. Your endpoint receives one HTTPS <C>POST</C> per prescription envelope and
          must return <C>2xx</C> only after the envelope is stored. This page states what your endpoint must
          accept, verify and return.
        </p>
      </header>

      <nav aria-label="Contents" style={{ ...card, padding: '14px 18px', margin: '0 0 8px' }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 6 }}>Contents</div>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {SECTIONS.map(([id, label]) => <li key={id} style={{ ...li, marginBottom: 2 }}><a href={`#${id}`}>{label}</a></li>)}
        </ol>
      </nav>

      <h2 id="endpoint" style={h2}>1. Endpoint requirements</h2>
      <p style={p}>Your endpoint must meet every requirement below before OwlRx delivers to it.</p>
      <RefTable head={['Requirement', 'What we expect']} rows={[
        ['Method', <C>POST</C>],
        ['URL', 'One HTTPS URL you host per environment: one for testing, one for production'],
        ['Transport', 'HTTPS only, TLS 1.2 or later. Plain HTTP is refused.'],
        ['Certificate', 'Issued by a publicly trusted certificate authority and valid for the URL’s hostname. Self-signed certificates are rejected.'],
        ['Reachability', 'A public DNS name that resolves to a public IP address. Private or internal addresses are refused.'],
        ['URL format', 'No credentials embedded in the URL'],
        ['Authentication', 'HTTP Basic, using a username and password you issue to OwlRx for this endpoint only'],
        ['Response time', 'Respond within 15 seconds of receiving the request'],
      ]} />

      <h2 id="what-we-send" style={h2}>2. What we send</h2>
      <p style={p}>Each request carries one envelope: one PioneerRx prescription event for a prescriber configured on your endpoint.</p>

      <h3 style={h3}>Headers</h3>
      <RefTable head={['Header', 'Value', 'Your use']} rows={[
        [<C>Authorization</C>, <C>{'Basic <base64(username:password)>'}</C>, 'Authenticate the request'],
        [<C>Content-Type</C>, <C>application/json; charset=utf-8</C>, '—'],
        [<C>X-OwlRx-Envelope</C>, 'SHA-256 of the request body, 64 lowercase hex characters', <a href="#integrity">Verify the body (section 3)</a>],
        [<C>X-OwlRx-Event</C>, <><C>pioneerrx.envelope</C>, or <C>owlrx.test</C> for a test delivery</>, 'Tell real deliveries from tests'],
        [<C>X-OwlRx-Delivery-Id</C>, 'A GUID; unchanged across retries of one delivery, new when we deliberately re-send an envelope', 'Quote it when you contact us about a delivery'],
        [<C>X-OwlRx-Signature</C>, <><C>{'sha256=<lowercase hex>'}</C>; present only when a signing secret has been agreed</>, <a href="#integrity">Verify the sender (section 3)</a>],
        [<C>User-Agent</C>, <C>OwlRx-Webhooks/1.0</C>, '—'],
      ]} />

      <h3 style={h3}>Body</h3>
      <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
        <li style={li}>A JSON object with <C>MessageHeader</C> and <C>Body</C>. <C>MessageHeader.MessageID</C> is a GUID unique to the envelope.</li>
        <li style={li}><C>Body</C> carries the <C>Rx</C>, <C>Patient</C>, <C>Prescribers</C>, <C>Claims</C> and <C>Facility</C> sections for the event.</li>
        <li style={li}>Values arrive as JSON strings, including numbers, amounts, quantities and some dates, for example <C>"DaysSupply": "30"</C>.</li>
        <li style={li}>Some fields are present but always null for the current event type.</li>
        <li style={li}>Only event 10 (prescription fill) is sent today. Other event types may be added and may carry new fields or sections; your endpoint must accept fields and sections it does not recognise.</li>
        <li style={li}>Field definitions follow the PioneerRx envelope format; refer to PioneerRx documentation.</li>
      </ul>

      <h3 style={h3}>Test deliveries</h3>
      <p style={p}>
        A test carries <C>X-OwlRx-Event: owlrx.test</C> and a synthetic, PHI-free body with the same headers and
        integrity hash. Answer it exactly as a real delivery (<C>2xx</C> when accepted), and do not store it as clinical data.
      </p>

      <h2 id="integrity" style={h2}>3. Integrity check</h2>
      <p style={p}>Verify every request before you store it. A request that fails a check must not be stored and must be answered with <C>503</C>.</p>
      <ol style={{ paddingLeft: 20, marginBottom: 12 }}>
        <li style={li}><strong>Body hash (required).</strong> The SHA-256 of the exact request body bytes as received, before any parsing, in lowercase hex, must equal <C>X-OwlRx-Envelope</C>.</li>
        <li style={li}><strong>Signature (only when a signing secret is agreed).</strong> The HMAC-SHA256 of the same bytes, keyed with the UTF-8 bytes of the shared secret, written as <C>sha256=</C> plus lowercase hex, must equal <C>X-OwlRx-Signature</C>.</li>
      </ol>

      <h2 id="response" style={h2}>4. Your response</h2>
      <p style={p}>
        Your HTTP status code is the acknowledgement. Return <C>2xx</C> only after the envelope is durably stored.
        To have an envelope sent again, including after a failed integrity check, return <C>503</C>.
      </p>
      <RefTable head={['Your response', 'What happens next']} rows={[
        [<C>2xx</C>, 'We send the next envelope'],
        [<><C>503</C> or any other <C>5xx</C></>, 'Not stored. We redeliver the same envelope later; nothing after it is sent in the meantime.'],
        [<><C>408</C>, <C>425</C> or <C>429</C></>, <>Same as <C>5xx</C></>],
        ['No response within 15 seconds', <>Same as <C>5xx</C></>],
        [<><C>401</C> or <C>403</C></>, 'Credentials rejected; not redelivered. Repeated rejections suspend delivery to your endpoint.'],
        [<>Any other <C>4xx</C> (for example 400, 404, 409, 422)</>, 'Permanently rejected; not redelivered. We send the next envelope.'],
      ]} />
      <p style={p}>Use a <C>4xx</C> other than <C>401</C> or <C>403</C> only for an envelope you will never accept.</p>

      <h3 style={h3}>Acknowledgement body (optional)</h3>
      <p style={p}>Return this JSON with your <C>2xx</C>, 1 KB maximum. A <C>2xx</C> with an empty body is also accepted.</p>
      <pre style={pre}>{ACK_EXAMPLE}</pre>

      <h2 id="delivery" style={h2}>5. Delivery behaviour</h2>
      <p style={p}>Delivery is ordered and at-least-once. Your endpoint must handle the following.</p>
      <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
        <li style={li}><strong>One at a time, in order.</strong> We send the next envelope only after the previous one is acknowledged (<C>2xx</C>) or permanently rejected. Order is the order envelopes were queued for your endpoint. An envelope we re-send arrives after newer ones, so do not assume <C>SentOnUTC</C> always increases.</li>
        <li style={li}><strong>At least once.</strong> The same envelope can arrive more than once. A repeat carries the same <C>MessageHeader.MessageID</C> and the same <C>X-OwlRx-Envelope</C>.</li>
        <li style={li}><strong>Idempotency (required).</strong> If you already stored an envelope with that <C>MessageID</C>, return <C>2xx</C> again and do not create a second record.</li>
        <li style={li}><strong>Redelivery.</strong> An unacknowledged envelope is sent again until you acknowledge it.</li>
        <li style={li}><strong>Suspension.</strong> Repeated failures or credential rejections suspend delivery to your endpoint until it is resolved with OwlRx.</li>
        <li style={li}><strong>Throughput.</strong> Expect up to 60 deliveries per minute per endpoint unless we agree otherwise.</li>
        <li style={li}><strong>Planned downtime.</strong> Tell us in advance, and return <C>503</C> while unavailable.</li>
      </ul>

      <h2 id="security" style={h2}>6. Security and data handling</h2>
      <p style={p}>Envelopes contain protected health information (PHI). These obligations apply before the first production delivery.</p>
      <RefTable head={['Area', 'Requirement']} rows={[
        ['Agreement', 'A Business Associate Agreement covering this data is in place before the first production envelope'],
        ['Credentials', 'OwlRx gets its own Basic-auth username and password, used for nothing else. The password is shared through a secure channel, never email, and rotated on request.'],
        ['Encryption in transit', <>HTTPS as set out in <a href="#endpoint">section 1</a></>],
        ['Encryption at rest', 'Stored envelopes are encrypted at rest'],
        ['Access', 'Access to envelope data is limited to staff who need it, as the BAA requires'],
        ['Logging', 'Envelope contents are not written to plain-text logs'],
        ['Test data', <>Test deliveries (<C>owlrx.test</C>) are kept apart from clinical records</>],
        ['Incidents', 'Any suspected breach is reported to the pharmacy and to OwlRx as the BAA requires'],
        ['Retention', 'Envelope data is kept and deleted as the BAA requires'],
      ]} />

      <footer style={{ marginTop: 48, paddingTop: 16, borderTop: '1px solid var(--color-border)', fontSize: 13, color: 'var(--color-text-muted)' }}>
        © Owl Consultations · <Link to="/legal/privacy">Privacy Policy</Link> · <Link to="/legal/terms">Terms of Use</Link>
      </footer>
    </main>
    </div>
  )
}
